import { memo, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DndContext } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import type { QuestionPreviewItem } from "src-next/shared/ui/questionnaire-form-renderer";

import type { QuestionnaireFormValues } from "../types";
import type { UseQuestionnaireBuilderResult } from "../hooks/useQuestionnaireBuilder";
import SortableQuestionListItem from "./SortableQuestionListItem";
import PreviewExpandDialog from "./PreviewExpandDialog";

const PREVIEW_EMPTY_MESSAGE =
  "As perguntas aparecerão aqui conforme você for adicionando no construtor.";

const EMPTY_OPTIONS_MESSAGE = "Defina as opções na área de edição da pergunta.";

export interface QuestionsListSectionProps {
  questionFields: UseQuestionnaireBuilderResult["questionFields"];
  watchedValues: QuestionnaireFormValues;
  activeQuestionIndex: number;
  sensors: UseQuestionnaireBuilderResult["sensors"];
  dndCollisionDetection: UseQuestionnaireBuilderResult["dndCollisionDetection"];
  dndSortingStrategy: UseQuestionnaireBuilderResult["dndSortingStrategy"];
  onDragEnd: UseQuestionnaireBuilderResult["handleDragEnd"];
  onDeleteQuestion: (index: number) => void;
  onDuplicateQuestion: (index: number) => void;
  onChangeActiveQuestion: (index: number) => void;
  onSaveConfig: () => void;
  intermediateSaveButtonLabel: string;
  onSubmitQuestionnaire: () => void;
  isDraftSubmitting: boolean;
  isQuestionnaireEmpty: boolean;
  isSubmitting: boolean;
  isTitleEmpty: boolean;
  hasUnsavedChanges: boolean;
  isConfigCollapsed: boolean;
}

const persistentQuestions = (watchedValues: QuestionnaireFormValues) =>
  watchedValues.questions.filter((question) => question.isPersistent !== false);

function toQuestionPreviewItems(
  questions: QuestionnaireFormValues["questions"],
): QuestionPreviewItem[] {
  return questions.map((question) => ({
    id: question.id,
    label: question.label,
    type: question.type,
    isRequired: question.isRequired,
    helperText: question.helperText,
    optionsCsv: question.optionsCsv,
    allowMultipleAnswers: question.allowMultipleAnswers,
  }));
}

const QuestionsListSection = memo(
  ({
    questionFields,
    watchedValues,
    activeQuestionIndex,
    sensors,
    dndCollisionDetection,
    dndSortingStrategy,
    onDragEnd,
    onDeleteQuestion,
    onDuplicateQuestion,
    onChangeActiveQuestion,
    onSaveConfig,
    intermediateSaveButtonLabel,
    onSubmitQuestionnaire,
    isDraftSubmitting,
    isQuestionnaireEmpty,
    isSubmitting,
    isTitleEmpty,
    hasUnsavedChanges,
    isConfigCollapsed,
  }: QuestionsListSectionProps) => {
    const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);

    const filteredFields = questionFields.filter((_, index) => {
      const question = watchedValues.questions[index];
      return question?.isPersistent !== false;
    });

    const sortableIds = filteredFields.map((field) => field.id);
    const questions = persistentQuestions(watchedValues);
    const previewItems = toQuestionPreviewItems(questions);
    const isIntermediateSaveDisabled =
      isDraftSubmitting || isTitleEmpty || !hasUnsavedChanges;
    const intermediateSaveButtonText = isDraftSubmitting
      ? "Salvando..."
      : !hasUnsavedChanges
        ? "Salvo"
        : intermediateSaveButtonLabel;

    const handleOpenPreviewDialog = useCallback(() => {
      setIsPreviewDialogOpen(true);
    }, []);

    const handleClosePreviewDialog = useCallback(() => {
      setIsPreviewDialogOpen(false);
    }, []);

    return (
      <>
        <Card
          variant="outlined"
          sx={{
            width: { xs: "100%", md: 550 },
            flexShrink: 0,

            display: "flex",
            flexDirection: "column",
          }}
        >
          <CardContent
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                Perguntas do questionário
              </Typography>
              <Button
                variant="outlined"
                onClick={handleOpenPreviewDialog}
                startIcon={<OpenInFullIcon />}
              >
                Pré-visualização
              </Button>
            </Stack>
            <List
              dense
              disablePadding
              sx={{
                borderRadius: 1,
                border: "1px dashed",
                borderColor: "divider",
                maxHeight: 400,
                overflowY: "scroll",
                overflowX: "hidden",
              }}
            >
              <DndContext
                sensors={sensors}
                collisionDetection={dndCollisionDetection}
                onDragEnd={onDragEnd}
              >
                <SortableContext
                  items={sortableIds}
                  strategy={dndSortingStrategy}
                >
                  {filteredFields.map((questionField) => {
                    const originalIndex = questionFields.findIndex(
                      (field) => field.id === questionField.id,
                    );
                    const question = watchedValues.questions[originalIndex];
                    const isActive = originalIndex === activeQuestionIndex;

                    return (
                      <SortableQuestionListItem
                        key={questionField.id}
                        id={questionField.id}
                        question={question}
                        isActive={isActive}
                        canDelete={questionFields.length > 1}
                        onClick={() => onChangeActiveQuestion(originalIndex)}
                        onDelete={(e) => {
                          e.stopPropagation();
                          onDeleteQuestion(originalIndex);
                        }}
                        onDuplicate={(e) => {
                          e.stopPropagation();
                          onDuplicateQuestion(originalIndex);
                        }}
                      />
                    );
                  })}
                  {questionFields.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary={
                          <Typography variant="caption" color="text.secondary">
                            Nenhuma pergunta adicionada ainda.
                          </Typography>
                        }
                      />
                    </ListItem>
                  )}
                </SortableContext>
              </DndContext>
            </List>

            <Box
              sx={{
                borderTop: 1,
                borderColor: "divider",
                mt: 2,
                pt: 2,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                flexWrap="wrap"
                spacing={1.5}
                sx={{
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={
                    isDraftSubmitting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SaveOutlinedIcon />
                    )
                  }
                  onClick={onSaveConfig}
                  disabled={isIntermediateSaveDisabled}
                  aria-busy={isDraftSubmitting}
                >
                  {intermediateSaveButtonText}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onSubmitQuestionnaire}
                  disabled={isSubmitting}
                  startIcon={<CheckCircleOutlineIcon />}
                >
                  {isSubmitting ? "Publicando…" : "Publicar questionário"}
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>

        {isPreviewDialogOpen && (
          <PreviewExpandDialog
            open={isPreviewDialogOpen}
            onClose={handleClosePreviewDialog}
            questions={previewItems}
            title={watchedValues.title}
            description={watchedValues.description}
            emptyMessage={PREVIEW_EMPTY_MESSAGE}
            emptyOptionsMessage={EMPTY_OPTIONS_MESSAGE}
          />
        )}
      </>
    );
  },
);

QuestionsListSection.displayName = "QuestionsListSection";

export default QuestionsListSection;
